<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ReportModel from '@models/Report';
import ServiceTimeModel from '@models/ServiceTime';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
      default: null,
    },
    // eslint-disable-next-line
    carUses: {
      required: true,
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      serviceTime: {
        car_use_id: null,
        date: null,
        approach_start: null,
        approach_end: null,
        service_start: null,
        service_end: null,
        return_start: null,
        return_end: null,
        idle_time: null,
        notes: null,
        paid: 0,
        // km_driven: null,
      },
      paidStatuses: [{ value: 0, text: 'Not paid' }, { value: 1, text: 'Paid' }],
      defaultServiceTime: {},
    };
  },
  computed: {
    ...mapState({
      dateConfigTime: (state) => state.common.timeConfig,
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  mounted() {
    this.defaultServiceTime = Object.assign({}, this.serviceTime);
    if (this.loadedId) {
      this.$wait.start('service-time loading');
      new ReportModel({ id: this.reportId })
        .serviceTimes()
        .find(this.loadedId)
        .then((response) => {
          this.serviceTime = Object.assign({}, this.serviceTime, response.service_time);
          this.$wait.end('service-time loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['serviceTime'], this.validate);
          });
        });
    }
  },
  methods: {
    assignServiceTimeDateValueFromTo(currentModel, nextModel) {
      if(currentModel != null) {
        this.serviceTime[nextModel] = this.serviceTime[currentModel]
      }
    },
    validate(callback) {
      this.$validator.validateAll('service-time-form').then((result) => {
        if (result) {
          this.$wait.start('service-time loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      const report = new ReportModel({ id: this.reportId });
      new ServiceTimeModel(this.serviceTime)
        .for(report)
        .save()
        .then((response) => {
          this.serviceTime = Object.assign({}, this.defaultServiceTime);
          this.$validator.reset();
          this.$emit('submitted', response.service_time);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('service-time loading');
        })
        .catch(() => this.$wait.end('service-time loading'));
    },
    carUseLabel(carUse) {
      let label = `#` + carUse.id;

      if(carUse.car) {
        return label + ` - ` + carUse.car.license_plate;
      }

      return label;
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('service-time loading')"></BlockingContainer>
    <b-form data-vv-scope="service-time-form" @submit.prevent="validate">
      <b-form-row>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.car_use') }}:
              <span class="status-star text-danger">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="carUsePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="carUsePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.car_use') }}
                </b-popover>
              </span>
            </template>
            <b-form-select
              v-model="serviceTime.car_use_id"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="car_use_id"
              :data-vv-as="$t('fields.car_use')"
              :state="errors.has('car_use_id', 'service-time-form') ? false : null"
              aria-describedby="input-feedback-car_use_id"
            >
              <option :value="null">{{ $t('placeholders.car_use') }}</option>
              <option v-for="carUse in carUses" :key="`carUseSelect${carUse.id}`" :value="carUse.id">
                {{ carUseLabel(carUse) }}
              </option>
            </b-form-select>
            <b-form-invalid-feedback id="input-feedback-car_use_id">
              <template v-show="errors.has('car_use_id', 'service-time-form')">{{ errors.first('car_use_id', 'service-time-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col sm="6">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.date') }}:
              <span class="status-star text-success">*</span>
              <span class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon id="serviceTimeDatePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover target="serviceTimeDatePopover" triggers="focus" placement="rightbottom">
                  {{ $t('infos.date') }}
                </b-popover>
              </span>
            </template>
            <div :class="{ 'is-invalid': errors.has('date', 'service-time-form') }">
              <flat-pickr
                v-model="serviceTime.date"
                v-validate="''"
                data-vv-name="date"
                :data-vv-as="$t('fields.date')"
                aria-describedby="input-feedback-date"
                :config="dateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('date', 'service-time-form') }"
                :placeholder="$t('placeholders.date')"
                @on-close="closeCalendar('serviceTime.date', 'dd.MM.yyyy', 'yyyy-MM-dd', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-date">
                <template v-show="errors.has('date', 'service-time-form')"
                  >{{ errors.first('date', 'service-time-form') }}
                </template>
              </b-form-invalid-feedback>
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="6">
          <b-form-row class="bg-success-light pt-1 pb-1">
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.approach_start') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="approachStartPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="approachStartPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.approach_start') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="approachStart"
                  v-model="serviceTime.approach_start"
                  v-validate="''"
                  data-vv-name="approach_start"
                  :data-vv-as="$t('fields.approach_start')"
                  aria-describedby="input-feedback-approach_start"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('approach_start', 'service-time-form') }"
                  :placeholder="$t('placeholders.approach_start')"
                  @on-open="selectAllOnOpen('approachStart')"
                  @on-close="closeCalendar('serviceTime.approach_start', 'HH:mm', 'HH:mm:ss', ...arguments); assignServiceTimeDateValueFromTo('approach_start', 'approach_end')"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-approach_start">
                  <template v-show="errors.has('approach_start', 'service-time-form')"
                    >{{ errors.first('approach_start', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.approach_end') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="approachEndPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="approachEndPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.approach_end') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="approachEnd"
                  v-model="serviceTime.approach_end"
                  v-validate="''"
                  data-vv-name="approach_end"
                  :data-vv-as="$t('fields.approach_end')"
                  aria-describedby="input-feedback-approach_end"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('approach_end', 'service-time-form') }"
                  :placeholder="$t('placeholders.approach_end')"
                  @on-open="selectAllOnOpen('approachEnd')"
                  @on-close="closeCalendar('serviceTime.approach_end', 'HH:mm', 'HH:mm:ss', ...arguments); assignServiceTimeDateValueFromTo('approach_end', 'service_start')"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-approach_end">
                  <template v-show="errors.has('approach_end', 'service-time-form')"
                    >{{ errors.first('approach_end', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-col>
        <b-col sm="6">
          <b-form-row class="bg-info-light pt-1 pb-1">
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.service_start') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="serviceStartPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="serviceStartPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.service_start') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="serviceStart"
                  v-model="serviceTime.service_start"
                  v-validate="''"
                  data-vv-name="service_start"
                  :data-vv-as="$t('fields.service_start')"
                  aria-describedby="input-feedback-service_start"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('service_start', 'service-time-form') }"
                  :placeholder="$t('placeholders.service_start')"
                  @on-open="selectAllOnOpen('serviceStart')"
                  @on-close="closeCalendar('serviceTime.service_start', 'HH:mm', 'HH:mm:ss', ...arguments); assignServiceTimeDateValueFromTo('service_start', 'service_end')"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-service_start">
                  <template v-show="errors.has('service_start', 'service-time-form')"
                    >{{ errors.first('service_start', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.service_end') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="serviceEndPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="serviceEndPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.service_end') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="serviceEnd"
                  v-model="serviceTime.service_end"
                  v-validate="''"
                  data-vv-name="service_end"
                  :data-vv-as="$t('fields.service_end')"
                  aria-describedby="input-feedback-service_end"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('service_end', 'service-time-form') }"
                  :placeholder="$t('placeholders.service_end')"
                  @on-open="selectAllOnOpen('serviceEnd')"
                  @on-close="closeCalendar('serviceTime.service_end', 'HH:mm', 'HH:mm:ss', ...arguments); assignServiceTimeDateValueFromTo('service_end', 'return_start')"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-service_end">
                  <template v-show="errors.has('service_end', 'service-time-form')"
                    >{{ errors.first('service_end', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col sm="6">
          <b-form-row class="bg-secondary-light pt-1 pb-1">
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.return_start') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="returnStartPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="returnStartPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.return_start') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="returnStart"
                  v-model="serviceTime.return_start"
                  v-validate="''"
                  data-vv-name="return_start"
                  :data-vv-as="$t('fields.return_start')"
                  aria-describedby="input-feedback-return_start"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('return_start', 'service-time-form') }"
                  :placeholder="$t('placeholders.return_start')"
                  @on-open="selectAllOnOpen('returnStart')"
                  @on-close="closeCalendar('serviceTime.service_end', 'HH:mm', 'HH:mm:ss', ...arguments); assignServiceTimeDateValueFromTo('return_start', 'return_end')"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-return_start">
                  <template v-show="errors.has('return_start', 'service-time-form')"
                    >{{ errors.first('return_start', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group>
                <template v-slot:label class="col-form-legend col-12">
                  {{ $t('fields.return_end') }}:
                  <span class="status-star text-success">*</span>
                  <span class="near-label-info text-secondary" @click.stop>
                    <FontAwesomeIcon id="returnEndPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                    <b-popover target="returnEndPopover" triggers="focus" placement="rightbottom">
                      {{ $t('infos.return_end') }}
                    </b-popover>
                  </span>
                </template>
                <flat-pickr
                  ref="returnEnd"
                  v-model="serviceTime.return_end"
                  v-validate="''"
                  data-vv-name="return_end"
                  :data-vv-as="$t('fields.return_end')"
                  aria-describedby="input-feedback-return_end"
                  :config="dateConfigTime"
                  class="form-control"
                  :class="{ 'is-invalid': errors.has('return_end', 'service-time-form') }"
                  :placeholder="$t('placeholders.return_end')"
                  @on-open="selectAllOnOpen('returnEnd')"
                  @on-close="closeCalendar('serviceTime.return_end', 'HH:mm', 'HH:mm:ss', ...arguments)"
                >
                </flat-pickr>
                <b-form-invalid-feedback id="input-feedback-return_end">
                  <template v-show="errors.has('return_end', 'service-time-form')"
                    >{{ errors.first('return_end', 'service-time-form') }}
                  </template>
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-form-row>
        </b-col>
        <b-col sm="3">
          <div class="pt-1 pb-1">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.idle_time') }}:
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="idleTimePopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="idleTimePopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.idle_time') }}
                  </b-popover>
                </span>
              </template>
              <flat-pickr
                ref="idleTime"
                v-model="serviceTime.idle_time"
                v-validate="''"
                data-vv-name="idle_time"
                :data-vv-as="$t('fields.idle_time')"
                aria-describedby="input-feedback-idle_time"
                :config="dateConfigTime"
                class="form-control"
                :class="{ 'is-invalid': errors.has('idle_time', 'service-time-form') }"
                :placeholder="$t('placeholders.idle_time')"
                @on-open="selectAllOnOpen('idleTime')"
                @on-close="closeCalendar('serviceTime.idle_time', 'HH:mm', 'HH:mm:ss', ...arguments)"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-idle_time">
                <template v-show="errors.has('idle_time', 'service-time-form')"
                  >{{ errors.first('idle_time', 'service-time-form') }}
                </template>
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </b-col>
      </b-form-row>

      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.notes') }}:
          <span class="status-star text-success">*</span>
          <span class="near-label-info text-secondary" @click.stop>
            <FontAwesomeIcon id="notesPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
            <b-popover target="notesPopover" triggers="focus" placement="rightbottom">
              {{ $t('infos.meeting_notes') }}
            </b-popover>
          </span>
        </template>
        <b-form-input
          id="serviceTimeTextarea"
          v-model="serviceTime.notes"
          v-validate="'max:65000'"
          data-vv-value-path="localValue"
          data-vv-name="notes"
          :data-vv-as="$t('fields.notes')"
          :placeholder="$t('placeholders.notes')"
          :state="errors.has('notes', 'service-time-form') ? false : null"
          aria-describedby="input-feedback-notes"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-notes">
          <template v-show="errors.has('notes', 'service-time-form')">
            {{ errors.first('notes', 'service-time-form') }}
          </template>
        </b-form-invalid-feedback>
      </b-form-group>

      <b-button ref="serviceTimeSubmitBtn" type="submit" :disabled="errors.any('service-time-form')" :variant="'outline-' + (!errors.any('service-time-form') ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>
